/**
 * Marketplace — buyer Browse of active listings (web).
 *
 * The buyer-facing storefront: a grid of ACTIVE listings published by sellers
 * (coops / farmers). Backed by the authed GET /marketplace/listings WITHOUT the
 * `mine` flag — the server returns the public/buyer browse of active listings
 * (the seller "my listings" view is the same endpoint with `mine: true`). The
 * list endpoint returns a { data, pagination } envelope, so we read `.data` off
 * the hand-rolled ListingListResponse (src/api/marketplace.ts — orval not yet
 * regenerated for /marketplace).
 *
 * Browse chrome (design-fidelity Tier 1 + Build #8): a pill SearchBar wired to
 * SERVER search (the `q` term rides the GET /marketplace/listings query) plus a
 * segmented filter/sort (All / Verified / Newest / Cheapest). Verified/newest/
 * cheapest stay CLIENT-SIDE sorts over the server-filtered page; the search term
 * is the one server param the contract surfaces today (price-bound + grade
 * filters are plumbed through listListings' signature for later UI). The query
 * is keyed on the search term so a new term refetches.
 *
 * Build #8 "stock-exchange" identity: a MarketStatBar replaces the old static
 * price band — latest mango price + a green/red %-change + a dependency-free
 * Sparkline of the public /market/prices/timeseries series. (A "recent sales"
 * ticker tape is intentionally OMITTED: there is no public sales/trade feed to
 * back it, and we do not fabricate data — see the note where the bar renders.)
 *
 * Each card links to the detail route /marketplace/$id, where a verified buyer
 * can place an offer, and carries a WatchStar (client-only watchlist, see
 * useWatchlist). Money is micro-ZARP (1e-6 ZARP) STRINGS on the wire; we convert
 * the ask price with microToZarp() and render with the @fse/ui <Money>. Each
 * card shows the listing's cover photo when present, else an eco-glyph fallback
 * tile (TD-135, the shared <ListingPhoto>). The nav item is
 * role-gated to `buyer` in _app.tsx (central wiring). i18n keys live under
 * marketplace.* in all four web locales.
 */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Icon,
  IconButton,
  MarketStatBar,
  mdSysColor,
  Money,
  SearchBar,
  SegmentedButton,
  Sparkline,
  Stack,
  StatusPill,
  Switch,
  Text,
  useColorMode,
} from '@fse/ui';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { getMyBuyerLocation, setMyBuyerLocation } from '../api/buyers';
import { FseApiError, readAccessTokenClaims } from '../api/client';
import {
  getPriceTimeSeries,
  microToZarp,
  type PriceTimeSeries,
} from '../api/market';
import { listListings, type ListingDto } from '../api/marketplace';
import { useBuyerRegion } from '../api/useBuyerRegion';
import { useWatchlist } from '../api/useWatchlist';
import { RegionSelect } from '../components/RegionSelect';
import { normalizeRegion, type SaProvince } from '../lib/regions';
import { ListingPhoto } from './ListingPhoto';

/**
 * Browse filter/sort modes. all/verified/newest/cheapest are CLIENT-side over the
 * server page; 'nearest' is a SERVER sort (sort=nearest) by the buyer's location.
 */
type BrowseMode = 'all' | 'verified' | 'newest' | 'cheapest' | 'nearest';

type DistanceBand = NonNullable<ListingDto['distanceBand']>;

/**
 * Human label for a coarse distance band (the only geo value the server exposes —
 * precise coordinates never leave the API). Bands are exclusive: le25km ≤25 km,
 * le50km 25–50, le100km 50–100, gt100km >100.
 */
function distanceBandLabel(t: TFunction, band: DistanceBand): string {
  switch (band) {
    case 'le25km':
      return t('marketplace.distance.le25km', 'Within 25 km');
    case 'le50km':
      return t('marketplace.distance.le50km', '25–50 km');
    case 'le100km':
      return t('marketplace.distance.le100km', '50–100 km');
    case 'gt100km':
      return t('marketplace.distance.gt100km', 'Over 100 km');
  }
}

/**
 * Read the seller's coarse region off a listing and normalise to a canonical SA
 * province (or null). `sellerRegion` is now typed on the generated `ListingDto`
 * (TD-124 regen), so this reads it directly.
 */
function readSellerRegion(listing: ListingDto): SaProvince | null {
  return normalizeRegion(listing.sellerRegion);
}

export function MarketplaceScreen() {
  const { t } = useTranslation();
  const watchlist = useWatchlist();
  const queryClient = useQueryClient();
  const { region: buyerRegion, setRegion: setBuyerRegion } = useBuyerRegion();

  // The precise-distance "near me" (location capture + bands + nearest sort) is
  // buyer-only — the /buyers/me/location endpoint is @Roles('buyer'). Other roles
  // browsing the marketplace keep the client-only province "near me" below.
  const isBuyer = readAccessTokenClaims()?.role === 'buyer';

  const [query, setQuery] = useState('');
  const [mode, setMode] = useState<BrowseMode>('all');
  // "Near me" toggle: when the buyer has picked a region, optionally HIDE
  // out-of-region listings instead of just floating in-region ones to the top.
  const [nearMeOnly, setNearMeOnly] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);

  // The buyer's stored location (drives whether distance bands appear + whether
  // 'nearest' sorts meaningfully). Buyer-only; soft-fail so a 403 for other roles
  // never breaks the browse.
  const locationQuery = useQuery({
    queryKey: ['buyer-location'],
    queryFn: ({ signal }) => getMyBuyerLocation(signal),
    enabled: isBuyer,
    retry: false,
  });
  const hasLocation = locationQuery.data?.location != null;

  // Capture the buyer's location via the browser Geolocation API and store it
  // server-side (precise, never shown to sellers — only coarse bands are). On
  // success refetch the listings so distance bands appear / 'nearest' re-sorts.
  const locationMutation = useMutation({
    mutationFn: setMyBuyerLocation,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['buyer-location'] });
      void queryClient.invalidateQueries({ queryKey: ['marketplace-listings'] });
    },
  });
  const captureLocation = useCallback(() => {
    setGeoError(null);
    if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
      setGeoError(t('marketplace.geo.unsupported', 'Location is not available in this browser.'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => locationMutation.mutate({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => setGeoError(t('marketplace.geo.denied', 'Location access was denied. Allow it to sort by distance.')),
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 60_000 },
    );
  }, [locationMutation, t]);
  const locating = locationMutation.isPending;

  // Debounce the search term so each keystroke doesn't fire a request; the
  // debounced value is what we key the query on + send to the server as `q`.
  const [debouncedQuery, setDebouncedQuery] = useState('');
  useEffect(() => {
    const h = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(h);
  }, [query]);

  // 'nearest' is a SERVER sort (closest seller first by the buyer's location);
  // the other modes sort the page client-side, so they ride sort=newest.
  const sort: 'newest' | 'nearest' = mode === 'nearest' ? 'nearest' : 'newest';

  // Buyer browse = active listings, no `mine`. SERVER search: the `q` term rides
  // the query (Lane F's listListings accepts q / minPriceZarpPerKgMicro /
  // maxPriceZarpPerKgMicro / grade — we only surface `q` in the UI today, the
  // rest stay undefined). The server returns a { data, pagination } envelope.
  const listingsQuery = useQuery({
    queryKey: ['marketplace-listings', 'browse', debouncedQuery, sort],
    queryFn: ({ signal }) =>
      listListings(
        { status: 'active', q: debouncedQuery || undefined, sort },
        signal,
      ),
    placeholderData: (prev) => prev, // keep the grid steady while a new term loads
  });

  // Price time series (mango) for the stock-exchange stat bar. Public feed;
  // soft-fails — if it errors we just hide the bar.
  const seriesQuery = useQuery({
    queryKey: ['market-price-series', 'mango', 30],
    queryFn: ({ signal }) => getPriceTimeSeries({ product: 'mango', days: 30 }, signal),
  });

  const listings = useMemo(() => listingsQuery.data?.data ?? [], [listingsQuery.data]);

  // Client-side sort/filter ON TOP of the server-filtered page. The text search
  // is now server-side (the `q` param), so we only apply the segment modes here,
  // then the buyer-region "near me" pass.
  const visible = useMemo(() => {
    let xs = listings;
    // "Verified" = sold by a cooperative (coop-vouched supply) — the closest
    // signal we have client-side without a per-seller KYC flag on ListingDto.
    if (mode === 'verified') xs = xs.filter((l) => l.sellerCoopId != null);
    if (mode === 'newest') {
      xs = [...xs].sort(
        (a, b) =>
          new Date(b.publishedAt ?? b.createdAt).getTime() -
          new Date(a.publishedAt ?? a.createdAt).getTime(),
      );
    }
    if (mode === 'cheapest') {
      xs = [...xs].sort(
        (a, b) =>
          microToZarp(a.askPriceZarpPerKgMicro) - microToZarp(b.askPriceZarpPerKgMicro),
      );
    }

    // "Near me" pass — only when the buyer has chosen a region AND isn't using the
    // precise 'nearest' server sort (which would otherwise be re-ordered by the
    // coarse province partition). With the toggle ON we FILTER to in-region
    // listings; otherwise we keep every listing but SORT in-region ones first (a
    // stable sort preserves the segment ordering within each group). Listings
    // without a sellerRegion sink to the bottom / hide when the toggle is on.
    if (buyerRegion && mode !== 'nearest') {
      const isNear = (l: ListingDto) => readSellerRegion(l) === buyerRegion;
      if (nearMeOnly) {
        xs = xs.filter(isNear);
      } else {
        // Stable partition: near-region first, far/unset after, original order kept.
        xs = [...xs].sort((a, b) => Number(isNear(b)) - Number(isNear(a)));
      }
    }
    return xs;
  }, [listings, mode, buyerRegion, nearMeOnly]);

  return (
    <Stack spacing={3}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Text variant="headline-medium" as="h2">
            {t('marketplace.title')}
          </Text>
          <Box sx={{ mt: 0.5 }}>
            <Text variant="body-medium" color="text.secondary">
              {listingsQuery.data
                ? t('marketplace.subtitleWithCount', { count: listings.length })
                : t('marketplace.subtitle')}
            </Text>
          </Box>
        </Box>
        <Link to="/watchlist" style={{ textDecoration: 'none' }}>
          <Button variant="outlined" startIcon={<Icon name="star" size={18} fill />}>
            {watchlist.count > 0
              ? t('watchlist.openWithCount', {
                  count: watchlist.count,
                  defaultValue: 'Watchlist ({{count}})',
                })
              : t('watchlist.open', 'Watchlist')}
          </Button>
        </Link>
      </Box>

      {/* Build #8 "stock-exchange" stat bar (mango). Hidden while pending / on
          error / with no observations. */}
      <MarketPriceStatBar query={seriesQuery} t={t} />

      {/* Browse chrome: search + segmented filter/sort. */}
      <Stack spacing={1.5}>
        <SearchBar
          value={query}
          onChange={setQuery}
          placeholder={t('marketplace.searchPlaceholder', 'Search crop or seller')}
          aria-label={t('marketplace.searchPlaceholder', 'Search crop or seller')}
        />
        <SegmentedButton
          aria-label={t('marketplace.filterLabel', 'Filter and sort listings')}
          value={mode}
          onChange={(next) => {
            if (typeof next === 'string') setMode(next as BrowseMode);
          }}
          options={[
            { value: 'all', label: t('marketplace.filter.all', 'All') },
            { value: 'verified', label: t('marketplace.filter.verified', 'Verified') },
            { value: 'newest', label: t('marketplace.filter.newest', 'Newest') },
            { value: 'cheapest', label: t('marketplace.filter.cheapest', 'Cheapest') },
            // Precise distance sort — buyer-only (needs the buyer's stored location).
            ...(isBuyer
              ? [{ value: 'nearest', label: t('marketplace.filter.nearest', 'Nearest') }]
              : []),
          ]}
        />

        {/* Precise "near me" (buyer-only): capture the buyer's location once so
            listings show a coarse distance band + the Nearest sort works. The
            point is stored server-side and NEVER shown to sellers — only bands. */}
        {isBuyer ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Icon name="pin" size={18} aria-hidden />}
              onClick={captureLocation}
              disabled={locating}
            >
              {locating
                ? t('marketplace.geo.locating', 'Locating…')
                : hasLocation
                  ? t('marketplace.geo.update', 'Update my location')
                  : t('marketplace.geo.use', 'Use my location')}
            </Button>
            {hasLocation ? (
              <Text variant="label-medium" color="text.secondary">
                {t('marketplace.geo.set', 'Location set — distances shown below')}
              </Text>
            ) : null}
            {geoError ? (
              <Text variant="label-medium" color="error.main">
                {geoError}
              </Text>
            ) : null}
          </Box>
        ) : null}

        {/* Buyer region — client-only "near me". Pick a province to float
            in-region listings to the top; the toggle (shown once a region is
            set) hides out-of-region listings entirely. Backed by useBuyerRegion
            (localStorage), no API. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ minWidth: 220, flexGrow: 1, maxWidth: 320 }}>
            <RegionSelect
              size="small"
              label={t('marketplace.region.label', 'Your region')}
              blankLabel={t('marketplace.region.all', 'All regions')}
              value={buyerRegion}
              onChange={(next) => {
                setBuyerRegion(next);
                if (next == null) setNearMeOnly(false); // toggle is meaningless without a region
              }}
            />
          </Box>
          {buyerRegion ? (
            <Switch
              checked={nearMeOnly}
              onChange={setNearMeOnly}
              label={t('marketplace.region.nearMeOnly', 'Near me only')}
            />
          ) : null}
        </Box>
      </Stack>

      {listingsQuery.isPending ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : listingsQuery.isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => listingsQuery.refetch()}>
              {t('marketplace.retry')}
            </Button>
          }
        >
          {t('marketplace.loadError', {
            error:
              listingsQuery.error instanceof FseApiError
                ? listingsQuery.error.message
                : String(listingsQuery.error),
          })}
        </Alert>
      ) : listings.length === 0 && debouncedQuery === '' && mode === 'all' ? (
        // True-empty: no listings at all and no active search/filter.
        <Card>
          <CardContent>
            <Stack spacing={1}>
              <Text variant="title-medium" as="h3">
                {t('marketplace.empty.title')}
              </Text>
              <Text variant="body-medium" color="text.secondary">
                {t('marketplace.empty.body')}
              </Text>
            </Stack>
          </CardContent>
        </Card>
      ) : visible.length === 0 ? (
        // A search term or filter excluded everything (server `q` returned none,
        // or a client segment filtered the page down to zero).
        <Card>
          <CardContent>
            <Stack spacing={1}>
              <Text variant="title-medium" as="h3">
                {t('marketplace.noMatch.title', 'No listings match')}
              </Text>
              <Text variant="body-medium" color="text.secondary">
                {t('marketplace.noMatch.body', 'Try a different search or filter.')}
              </Text>
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 2,
          }}
        >
          {visible.map((listing) => (
            <ListingCard
              key={listing.id}
              listing={listing}
              t={t}
              watched={watchlist.isWatched(listing.id)}
              onToggleWatch={() => watchlist.toggle(listing.id)}
            />
          ))}
        </Box>
      )}
    </Stack>
  );
}

interface MarketPriceStatBarProps {
  query: UseQueryResult<PriceTimeSeries, unknown>;
  t: TFunction;
}

/**
 * Build #8 "stock-exchange" stat bar — latest mango price + a green/red
 * %-change + a dependency-free Sparkline of the public price time series
 * (GET /market/prices/timeseries). Soft-fails: hidden while pending / on error /
 * when the window has no observations.
 *
 * RECENT-SALES TICKER (intentionally OMITTED): a scrolling "recent trades" tape
 * would need a public sales/trade feed (settled offers/orders). No such public
 * endpoint exists today, and we do not fabricate data — so the tape is left out
 * and noted here as a real gap. The price series IS real data, so the sparkline
 * and %-change are honest.
 */
function MarketPriceStatBar({ query, t }: MarketPriceStatBarProps) {
  const { mode } = useColorMode();
  const series = query.data;
  if (query.isPending || query.isError || !series || series.points.length === 0) return null;

  const points = series.points;
  const latestMicro = points[points.length - 1]!.micro;
  const latest = microToZarp(latestMicro);
  // Sparkline wants plain numbers (whole-ZARP keeps the values small + finite).
  const sparkValues = points.map((p) => microToZarp(p.micro));

  const pct = series.pctChange;
  const direction: 'up' | 'down' | 'neutral' =
    pct == null || Math.abs(pct) < 0.05 ? 'neutral' : pct > 0 ? 'up' : 'down';
  // Sign-prefixed, 1-dp percent. null (a single point) → no delta shown.
  const deltaLabel =
    pct == null
      ? undefined
      : t('marketplace.statBar.delta', {
          value: `${pct > 0 ? '+' : ''}${pct.toFixed(1)}`,
          defaultValue: '{{value}}%',
        });

  const asOfLabel = series.asOf
    ? new Date(series.asOf).toLocaleDateString('en-ZA', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : null;
  const meta = [
    series.grade
      ? t('marketplace.gradeValue', { grade: series.grade })
      : t('marketplace.statBar.allGrades', 'All grades'),
    asOfLabel
      ? t('marketplace.priceBand.asOf', { date: asOfLabel, defaultValue: 'As of {{date}}' })
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

  // Sparkline trend colour follows the delta direction for a stronger
  // stock-exchange read: down = error (red), otherwise the primary leaf green.
  // mdSysColor resolves the actual hex per light/dark mode.
  const sys = mdSysColor[mode];
  const sparkColor = direction === 'down' ? sys.error : sys.primary;
  return (
    <MarketStatBar
      icon="payments"
      label={t('marketplace.priceBand.title', "Today's mango price")}
      value={<Money amount={latest} unit={t('marketplace.perKg')} size="xl" />}
      delta={deltaLabel}
      deltaDirection={direction}
      meta={meta}
      chart={
        <Sparkline
          points={sparkValues}
          width={140}
          height={40}
          color={sparkColor}
          ariaLabel={t('marketplace.statBar.sparkAria', 'Mango price trend, last 30 days')}
        />
      }
    />
  );
}

interface ListingCardProps {
  listing: ListingDto;
  t: TFunction;
  watched: boolean;
  onToggleWatch: () => void;
}

/**
 * Restyled listing card following the design info hierarchy: a cover-image tile
 * (real photo when present, else an eco-glyph fallback — TD-135), then crop +
 * grade, quantity, ask price, seller, and an offer-count status pill. A
 * WatchStar floats over the image tile. The whole card links to
 * the detail route; the star button stops propagation so a tap toggles the
 * watch without navigating.
 */
function ListingCard({ listing, t, watched, onToggleWatch }: ListingCardProps) {
  const askPerKg = microToZarp(listing.askPriceZarpPerKgMicro);
  const seller = listing.sellerCoopName ?? listing.sellerName;
  const sellerRegion = readSellerRegion(listing);

  return (
    <Card sx={{ height: '100%', position: 'relative', overflow: 'hidden' }}>
      {/* Cover image (TD-135) when the listing has a photo, else the brand-tinted
          eco-glyph fallback. ListingPhoto owns the tile dimensions + fallback. */}
      <ListingPhoto
        listingId={listing.id}
        hasPhoto={listing.hasPhoto}
        height={120}
        glyphSize={48}
        alt={listing.product}
      />

      {/* WatchStar — floated top-right over the image. stopPropagation so the
          tap toggles the watch instead of following the card link. */}
      <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
        <IconButton
          size="small"
          aria-label={
            watched
              ? t('watchlist.remove', 'Remove from watchlist')
              : t('watchlist.add', 'Add to watchlist')
          }
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onToggleWatch();
          }}
          sx={{
            backgroundColor: 'background.paper',
            color: watched ? 'primary.main' : 'text.secondary',
            '&:hover': { backgroundColor: 'background.paper' },
          }}
        >
          <Icon name="star" size={20} fill={watched} color="inherit" />
        </IconButton>
      </Box>

      <Link
        to="/marketplace/$id"
        params={{ id: listing.id }}
        style={{ textDecoration: 'none', display: 'block' }}
      >
        <CardContent sx={{ cursor: 'pointer' }}>
          <Stack spacing={1.5}>
            {/* Product + grade */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <Text variant="title-large" as="span" truncate>
                {listing.product}
              </Text>
              {listing.grade ? (
                <Chip
                  kind="assist"
                  color="neutral"
                  label={t('marketplace.gradeValue', { grade: listing.grade })}
                />
              ) : null}
            </Box>

            {/* Quantity */}
            <Box>
              <Text variant="label-medium" color="text.secondary">
                {t('marketplace.quantity')}
              </Text>
              <Box sx={{ mt: 0.25 }}>
                <Text variant="title-medium" as="span">
                  {t('marketplace.qtyKg', { qty: listing.qtyKg })}
                </Text>
              </Box>
            </Box>

            {/* Ask price */}
            <Box>
              <Text variant="label-medium" color="text.secondary">
                {t('marketplace.askPrice')}
              </Text>
              <Box sx={{ mt: 0.25 }}>
                <Money amount={askPerKg} unit={t('marketplace.perKg')} size="lg" />
              </Box>
            </Box>

            <Divider />

            {/* Seller + offer count */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              <Text variant="label-medium" color="text.secondary">
                {t('marketplace.seller', { name: seller })}
              </Text>
              <StatusPill variant="upcoming" icon="sell">
                {t('marketplace.offerCount', { count: listing.offerCount })}
              </StatusPill>
            </Box>

            {/* Seller region (geo-region MVP) — a small location line with a
                map-pin glyph. Only shown when the seller has set a region
                (sellerRegion is null/unset otherwise). */}
            {sellerRegion ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Icon name="pin" size={16} color="inherit" aria-hidden />
                <Text variant="label-medium" color="text.secondary">
                  {sellerRegion}
                </Text>
              </Box>
            ) : null}

            {/* Coarse distance band from the signed-in buyer (when they've set a
                location + the seller has GPS). Derived server-side; precise
                coordinates are never exposed. Rendered like the region line. */}
            {listing.distanceBand ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Icon name="pin" size={16} color="inherit" aria-hidden />
                <Text variant="label-medium" color="primary.main">
                  {distanceBandLabel(t, listing.distanceBand)}
                </Text>
              </Box>
            ) : null}
          </Stack>
        </CardContent>
      </Link>
    </Card>
  );
}
